import { Link, useMatches } from "@tanstack/react-router";
import { LayoutGridIcon } from "lucide-react";
import { createContext, useContext, useEffect, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import { WhatsappIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Separator } from "@/components/ui/separator";
import {
  ACTIVITIES__NEW__,
  NAV_ITEMS__NEW__,
  type NavItem__NEW__,
} from "@/lib/constants";
import { Image } from "../image";
import { useActiveActivity } from "./desktop-nav";

export function MobileNav() {
  const [open, setOpen] = useState(false);

  const matches = useMatches();
  useEffect(() => {
    setOpen(false);
  }, [matches]);

  return (
    <div
      className="!pb-[calc(env(safe-area-inset-bottom)+1rem)] fixed inset-x-0 bottom-0 z-10 flex items-center justify-end bg-transparent p-4 text-accent-foreground md:hidden"
      key="mobile-nav"
    >
      <Drawer direction="bottom" onOpenChange={setOpen} open={open}>
        <DrawerTrigger asChild>
          <button
            className="-top-4 absolute left-4 flex size-20 shrink-0 flex-col items-center justify-center rounded-full border border-accent-foreground bg-accent-foreground text-accent shadow-lg active:scale-[0.98]"
            type="button"
          >
            <LayoutGridIcon className="size-8" />
            <span>Menü</span>
          </button>
        </DrawerTrigger>
        <NavSheet />
      </Drawer>
      <BookingButton />
    </div>
  );
}

function BookingButton() {
  const activity = useActiveActivity();
  const { label, ...bookingLink } =
    activity && "bookingLink" in activity
      ? activity.bookingLink
      : {
          label: "Jetzt buchen",
          to: "/buchen",
        };
  const backgroundColor = activity?.brand.primaryColor || "var(--primary)";
  const foregroundColor =
    activity?.brand.primaryForegroundColor || "var(--primary-foreground)";

  return (
    <Button
      asChild
      className="w-[calc(100%-6rem)] bg-[var(--booking-button-background-color)] text-[var(--booking-button-foreground-color)] shadow-lg hover:bg-[var(--booking-button-background-color)]/90"
      size="xl"
      style={
        {
          "--booking-button-background-color": backgroundColor,
          "--booking-button-foreground-color": foregroundColor,
        } as React.CSSProperties
      }
    >
      <Link {...bookingLink}>{label}</Link>
    </Button>
  );
}

function NavSheet() {
  return (
    <DrawerContent className="pb-12">
      <Image
        alt="Brick texture"
        className="-z-10 absolute top-0 left-0 w-1/2"
        height={335}
        src="/images/brick_texture.png"
        width={200}
      />
      <DrawerHeader className="pt-8 pb-10">
        <Link to="/">
          <Image
            alt="Logo"
            className="h-7 w-full object-contain"
            height={56}
            src="/images/logo.png"
            width={358}
          />
        </Link>
        <DrawerTitle className="sr-only">Menü</DrawerTitle>
        <DrawerDescription className="sr-only">
          Menü für mobile Geräte
        </DrawerDescription>
      </DrawerHeader>
      <nav className="px-6">
        <div className="grid grid-cols-4 gap-2">
          {Object.values(ACTIVITIES__NEW__).map((activity) => {
            const { link } = activity;
            const Component = "to" in link ? Link : "a";
            return (
              <Component
                key={activity.key}
                {...link}
                className="grid gap-2 rounded pb-2 outline-[var(--outline-color)] data-[status=active]:outline-2"
                hashScrollIntoView={false}
                resetScroll
                style={
                  {
                    "--outline-color": activity.brand.primaryColor,
                  } as React.CSSProperties
                }
              >
                <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-lg">
                  <Image
                    alt={activity.name}
                    className="absolute inset-0 size-full object-cover object-center"
                    height={80}
                    src={activity.brand.backgroundImage}
                    width={80}
                  />
                  <div className="absolute inset-0 bg-black/50" />
                  <Image
                    alt={activity.name}
                    className="relative size-4/5 object-contain object-center"
                    height={64}
                    src={activity.brand.logo}
                    width={64}
                  />
                </div>
                <span className="text-center text-sm">{activity.name}</span>
              </Component>
            );
          })}
        </div>
        <Separator className="my-4" />
        <ul>
          {NAV_ITEMS__NEW__.map((item) => {
            if (item.items) {
              return (
                <Fragment key={item.key}>
                  {item.items.map((childItem) => (
                    <li key={childItem.key}>
                      <NavItem item={childItem} />
                    </li>
                  ))}
                  <Separator className="my-4" />
                </Fragment>
              );
            }

            return (
              <li key={item.key}>
                <NavItem item={item} />
              </li>
            );
          })}
          {/* <Separator className="my-4" />
          <WhatsAppButton /> */}
        </ul>
      </nav>
    </DrawerContent>
  );
}

function NavItem({ item }: { item: NavItem__NEW__ }) {
  return (
    <Button
      asChild
      className="w-full justify-start"
      size="default"
      variant="ghost"
    >
      <Link {...item.link}>{item.label}</Link>
    </Button>
  );
}

function WhatsAppButton() {
  return (
    <a
      className="flex items-center gap-2"
      href="https://wa.me/491702441822"
      rel="noreferrer"
      target="_blank"
    >
      <div className="flex items-center justify-center rounded-lg bg-[#25D366] p-2">
        <WhatsappIcon className="size-8" />
      </div>
      <div className="">
        <span className="block text-sm leading-none">Du hast Fragen?</span>
        <span className="block font-bold text-base leading-none">
          Schreib uns bei WhatsApp!
        </span>
      </div>
    </a>
  );
}

